import React, { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { workplansAPI, organizationsAPI } from '../services/api';
import { Loading, EmptyState, ConfirmDialog } from '../components/common';
import Modal from '../components/common/Modal';
import { formatDate, formatFileSize } from '../utils/formatters';
import { CURRENT_YEAR, YEARS, YEARS7 } from '../utils/constants';
import toast from 'react-hot-toast';
import {
  CalendarIcon,
  PlusIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationCircleIcon,
  PencilIcon,
  TrashIcon,
  PaperClipIcon,
  ArrowUpTrayIcon,
  XMarkIcon,
  EyeIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';

const ITEM_STATUSES = {
  pending: { label: 'Kutilmoqda', color: 'bg-gray-100 text-gray-700', icon: ClockIcon },
  in_progress: { label: 'Bajarilmoqda', color: 'bg-blue-100 text-blue-700', icon: ClockIcon },
  completed: { label: 'Bajarildi', color: 'bg-green-100 text-green-700', icon: CheckCircleIcon },
  not_completed: { label: 'Bajarilmadi', color: 'bg-red-100 text-red-700', icon: ExclamationCircleIcon },
};

const WorkPlans = () => {
  const { user } = useSelector((state) => state.auth);
  const isAdmin = user?.role === 'admin';

  // State
  const [workplans, setWorkplans] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedPlan, setExpandedPlan] = useState(null);
  const [expandedItem, setExpandedItem] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedExecution, setSelectedExecution] = useState(null);

  // Filters
  const [year, setYear] = useState(CURRENT_YEAR);
  const [filterOrg, setFilterOrg] = useState('');

  // Modals
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [showItemModal, setShowItemModal] = useState(false);
  const [showExecutionModal, setShowExecutionModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteType, setDeleteType] = useState('');

  // Form data
  const [planForm, setPlanForm] = useState({
    title: '', plan_type: 'otm', organization: '', year: CURRENT_YEAR, quarter: 0, description: ''
  });
  const [itemForm, setItemForm] = useState({
    title: '', description: '', responsible: '', start_date: '', end_date: '', status: 'pending'
  });
  const [executionForm, setExecutionForm] = useState({
    execution_date: '', description: '', result: '', findings_count: 0, findings_description: '', recommendations: ''
  });
  const [uploadFiles, setUploadFiles] = useState([]);

  // Fetch data
  const fetchWorkplans = useCallback(async () => {
    setLoading(true);
    try {
      const params = { year };
      if (filterOrg) params.organization = filterOrg;
      const response = await workplansAPI.getAll(params);
      setWorkplans(response.data.results || response.data || []);
    } catch (error) {
      console.error('Workplans fetch error:', error);
      toast.error('Ma\'lumotlarni yuklashda xatolik');
    } finally {
      setLoading(false);
    }
  }, [year, filterOrg]);

  const fetchOrganizations = async () => {
    try {
      const response = await organizationsAPI.getAll();
      setOrganizations(response.data.results || response.data || []);
    } catch (error) {
      console.error('Organizations fetch error:', error);
    }
  };

  useEffect(() => {
    fetchWorkplans();
    if (isAdmin) fetchOrganizations();
  }, [fetchWorkplans, isAdmin]);

  // Load full plan details when expanded
  const handleExpandPlan = async (planId) => {
    if (expandedPlan === planId) {
      setExpandedPlan(null);
      return;
    }
    try {
      const response = await workplansAPI.getOne(planId);
      const planIndex = workplans.findIndex(p => p.id === planId);
      if (planIndex !== -1) {
        const updated = [...workplans];
        updated[planIndex] = response.data;
        setWorkplans(updated);
      }
      setExpandedPlan(planId);
    } catch (error) {
      console.error('Plan details error:', error);
    }
  };

  // Plan CRUD
  const handlePlanSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedPlan) {
        await workplansAPI.update(selectedPlan.id, planForm);
        toast.success('Ish reja yangilandi!');
      } else {
        await workplansAPI.create(planForm);
        toast.success('Ish reja yaratildi!');
      }
      setShowPlanModal(false);
      resetPlanForm();
      fetchWorkplans();
    } catch (error) {
      toast.error('Xatolik yuz berdi');
    }
  };

  const handleEditPlan = (plan) => {
    setSelectedPlan(plan);
    setPlanForm({
      title: plan.title,
      plan_type: plan.plan_type,
      organization: plan.organization,
      year: plan.year,
      quarter: plan.quarter || 0,
      description: plan.description || ''
    });
    setShowPlanModal(true);
  };

  const resetPlanForm = () => {
    setSelectedPlan(null);
    setPlanForm({ title: '', plan_type: 'otm', organization: user?.organization || '', year: CURRENT_YEAR, quarter: 0, description: '' });
  };

  // Item CRUD
  const handleItemSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedItem) {
        await workplansAPI.updateItem(expandedPlan, selectedItem.id, itemForm);
        toast.success('Band yangilandi!');
      } else {
        await workplansAPI.createItem(expandedPlan, { ...itemForm, workplan: expandedPlan });
        toast.success('Band qo\'shildi!');
      }
      setShowItemModal(false);
      resetItemForm();
      handleExpandPlan(expandedPlan);
    } catch (error) {
      toast.error('Xatolik yuz berdi');
    }
  };

  const handleEditItem = (item) => {
    setSelectedItem(item);
    setItemForm({
      title: item.title,
      description: item.description || '',
      responsible: item.responsible || '',
      start_date: item.start_date,
      end_date: item.end_date,
      status: item.status
    });
    setShowItemModal(true);
  };

  const handleCompleteItem = async (item) => {
    try {
      await workplansAPI.completeItem(expandedPlan, item.id);
      toast.success('Band bajarildi deb belgilandi!');
      handleExpandPlan(expandedPlan);
    } catch (error) {
      toast.error('Xatolik yuz berdi');
    }
  };

  const resetItemForm = () => {
    setSelectedItem(null);
    setItemForm({ title: '', description: '', responsible: '', start_date: '', end_date: '', status: 'pending' });
  };

  // Execution CRUD
  const handleExecutionSubmit = async (e) => {
    e.preventDefault();
    try {
      let execution;
      if (selectedExecution) {
        const response = await workplansAPI.updateExecution(expandedItem, selectedExecution.id, executionForm);
        execution = response.data;
        toast.success('Bajarilgan ish yangilandi!');
      } else {
        const response = await workplansAPI.createExecution(expandedItem, executionForm);
        execution = response.data;
        toast.success('Bajarilgan ish qo\'shildi!');
      }

      // Upload files if any
      if (uploadFiles.length > 0 && execution?.id) {
        const formData = new FormData();
        uploadFiles.forEach(f => formData.append('files', f));
        await workplansAPI.uploadFiles(execution.id, formData);
        toast.success(`${uploadFiles.length} ta fayl yuklandi`);
      }

      setShowExecutionModal(false);
      resetExecutionForm();
      handleExpandPlan(expandedPlan);
    } catch (error) {
      console.error('Execution error:', error);
      toast.error('Xatolik yuz berdi');
    }
  };

  const handleEditExecution = (execution) => {
    setSelectedExecution(execution);
    setExecutionForm({
      execution_date: execution.execution_date,
      description: execution.description,
      result: execution.result || '',
      findings_count: execution.findings_count || 0,
      findings_description: execution.findings_description || '',
      recommendations: execution.recommendations || ''
    });
    setShowExecutionModal(true);
  };

  const resetExecutionForm = () => {
    setSelectedExecution(null);
    setExecutionForm({ execution_date: '', description: '', result: '', findings_count: 0, findings_description: '', recommendations: '' });
    setUploadFiles([]);
  };

  // Delete handlers
  const handleDelete = async () => {
    try {
      if (deleteType === 'plan' && selectedPlan) {
        await workplansAPI.delete(selectedPlan.id);
        toast.success('Ish reja o\'chirildi!');
        fetchWorkplans();
      } else if (deleteType === 'item' && selectedItem) {
        await workplansAPI.deleteItem(expandedPlan, selectedItem.id);
        toast.success('Band o\'chirildi!');
        handleExpandPlan(expandedPlan);
      } else if (deleteType === 'execution' && selectedExecution) {
        await workplansAPI.deleteExecution(expandedItem, selectedExecution.id);
        toast.success('Bajarilgan ish o\'chirildi!');
        handleExpandPlan(expandedPlan);
      }
      setShowDeleteConfirm(false);
      setSelectedPlan(null);
      setSelectedItem(null);
      setSelectedExecution(null);
    } catch (error) {
      toast.error('O\'chirishda xatolik');
    }
  };

  const confirmDelete = (type, item) => {
    setDeleteType(type);
    if (type === 'plan') setSelectedPlan(item);
    else if (type === 'item') setSelectedItem(item);
    else if (type === 'execution') setSelectedExecution(item);
    setShowDeleteConfirm(true);
  };

  // File handlers
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setUploadFiles(prev => [...prev, ...files]);
  };

  const removeFile = (index) => {
    setUploadFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleDeleteFile = async (executionId, fileId) => {
    try {
      await workplansAPI.deleteFile(executionId, fileId);
      toast.success('Fayl o\'chirildi');
      handleExpandPlan(expandedPlan);
    } catch (error) {
      toast.error('Fayl o\'chirishda xatolik');
    }
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const s = ITEM_STATUSES[status] || ITEM_STATUSES.pending;
    const Icon = s.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${s.color}`}>
        <Icon className="w-3.5 h-3.5" />
        {s.label}
      </span>
    );
  };

  if (loading) return <Loading />;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Ish reja</h1>
          <p className="text-gray-500">{workplans.length} ta ish reja</p>
        </div>
        <button onClick={() => { resetPlanForm(); setShowPlanModal(true); }} className="btn-primary">
          <PlusIcon className="w-5 h-5" /> Yangi ish reja
        </button>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex flex-wrap gap-4">
          <select className="select w-auto" value={year} onChange={(e) => setYear(parseInt(e.target.value))}>
            {YEARS.map(y => (
              <option key={y} value={y}>{y}-yil </option>
            ))}
          </select>
          {isAdmin && (
            <select className="select w-auto" value={filterOrg} onChange={(e) => setFilterOrg(e.target.value)}>
              <option value="">Barcha tashkilotlar</option>
              {organizations.map(org => (
                <option key={org.id} value={org.id}>{org.short_name || org.name}</option>
              ))}
            </select>
          )}
        </div>
      </div>

      {/* Workplans List */}
      {workplans.length > 0 ? (
        <div className="space-y-4">
          {workplans.map((plan) => (
            <div key={plan.id} className="card p-0 overflow-hidden">
              {/* Plan Header */}
              <div
                className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50"
                onClick={() => handleExpandPlan(plan.id)}
              >
                <div className="flex items-center gap-4">
                  {expandedPlan === plan.id ? (
                    <ChevronDownIcon className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronRightIcon className="w-5 h-5 text-gray-400" />
                  )}
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <CalendarIcon className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800">{plan.title}</h3>
                    <p className="text-sm text-gray-500">
                      {plan.organization_name} • {plan.year}-yil 
                      {plan.quarter > 0 && ` • ${plan.quarter}-chorak`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right hidden sm:block">
                    <p className="text-sm font-medium">{plan.items_count || 0} ta band</p>
                    <p className="text-sm text-gray-500">{plan.completion_rate || 0}% bajarildi</p>
                  </div>
                  <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden hidden sm:block">
                    <div
                      className="h-full bg-green-500 rounded-full"
                      style={{ width: `${plan.completion_rate || 0}%` }}
                    />
                  </div>
                  <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                    <button onClick={() => handleEditPlan(plan)} className="p-2 hover:bg-gray-100 rounded-lg">
                      <PencilIcon className="w-4 h-4 text-gray-600" />
                    </button>
                    {isAdmin && (
                      <button onClick={() => confirmDelete('plan', plan)} className="p-2 hover:bg-gray-100 rounded-lg">
                        <TrashIcon className="w-4 h-4 text-red-600" />
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Expanded Items */}
              {expandedPlan === plan.id && (
                <div className="border-t bg-gray-50">
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-medium text-gray-700">Ish reja bandlari</h4>
                      <button
                        onClick={() => { resetItemForm(); setShowItemModal(true); }}
                        className="btn-outline btn-sm"
                      >
                        <PlusIcon className="w-4 h-4" /> Band qo'shish
                      </button>
                    </div>

                    {plan.items && plan.items.length > 0 ? (
                      <div className="space-y-3">
                        {plan.items.map((item) => (
                          <div key={item.id} className="bg-white rounded-lg border">
                            {/* Item Header */}
                            <div
                              className="p-4 flex items-start justify-between cursor-pointer hover:bg-gray-50"
                              onClick={() => setExpandedItem(expandedItem === item.id ? null : item.id)}
                            >
                              <div className="flex items-start gap-3 flex-1">
                                <span className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center text-sm font-medium text-gray-600">
                                  {item.order_number}
                                </span>
                                <div className="flex-1">
                                  <p className="font-medium text-gray-800">{item.title}</p>
                                  <div className="flex flex-wrap items-center gap-2 mt-1">
                                    {getStatusBadge(item.status)}
                                    <span className="text-xs text-gray-500">
                                      {formatDate(item.start_date)} - {formatDate(item.end_date)}
                                    </span>
                                    {item.responsible && (
                                      <span className="text-xs text-gray-500">• {item.responsible}</span>
                                    )}
                                    {item.executions_count > 0 && (
                                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                                        {item.executions_count} ta ish
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                                {/* Muddati o'tgan ko'rsatkich */}
                                {item.is_overdue && (
                                  <span className="flex items-center gap-1 text-xs text-orange-600 bg-orange-50 px-2 py-1 rounded mr-2">
                                    <ClockIcon className="w-3.5 h-3.5" />
                                    Muddati o'tgan
                                  </span>
                                )}
                                {/* Faqat admin bajarildi deb belgilay oladi */}
                                {isAdmin && item.status !== 'completed' && (
                                  <button
                                    onClick={() => handleCompleteItem(item)}
                                    className="p-2 hover:bg-green-50 rounded-lg"
                                    title="Bajarildi deb belgilash"
                                  >
                                    <CheckIcon className="w-4 h-4 text-green-600" />
                                  </button>
                                )}
                                <button onClick={() => handleEditItem(item)} className="p-2 hover:bg-gray-100 rounded-lg">
                                  <PencilIcon className="w-4 h-4 text-gray-600" />
                                </button>
                                {isAdmin && (
                                  <button onClick={() => confirmDelete('item', item)} className="p-2 hover:bg-gray-100 rounded-lg">
                                    <TrashIcon className="w-4 h-4 text-red-600" />
                                  </button>
                                )}
                              </div>
                            </div>

                            {/* Expanded Executions */}
                            {expandedItem === item.id && (
                              <div className="border-t bg-gray-50 p-4">
                                <div className="flex items-center justify-between mb-3">
                                  <h5 className="text-sm font-medium text-gray-600">Bajarilgan ishlar</h5>
                                  <button
                                    onClick={() => { setExpandedItem(item.id); resetExecutionForm(); setShowExecutionModal(true); }}
                                    className="btn-outline btn-sm"
                                  >
                                    <PlusIcon className="w-4 h-4" /> Ish qo'shish
                                  </button>
                                </div>

                                {item.executions && item.executions.length > 0 ? (
                                  <div className="space-y-3">
                                    {item.executions.map((exec) => (
                                      <div key={exec.id} className="bg-white p-4 rounded-lg border">
                                        <div className="flex items-start justify-between">
                                          <div className="flex-1">
                                            <div className="flex items-center gap-2 mb-2">
                                              <span className="text-sm font-medium text-gray-800">
                                                {formatDate(exec.execution_date)}
                                              </span>
                                              <span className="text-xs text-gray-500">
                                                {exec.executor_name}
                                              </span>
                                            </div>
                                            <p className="text-sm text-gray-700 mb-2">{exec.description}</p>
                                            
                                            {exec.findings_count > 0 && (
                                              <div className="bg-yellow-50 p-2 rounded mb-2">
                                                <p className="text-xs font-medium text-yellow-800">
                                                  Aniqlangan holatlar: {exec.findings_count}
                                                </p>
                                                {exec.findings_description && (
                                                  <p className="text-xs text-yellow-700 mt-1">{exec.findings_description}</p>
                                                )}
                                              </div>
                                            )}
                                            
                                            {exec.recommendations && (
                                              <div className="bg-blue-50 p-2 rounded mb-2">
                                                <p className="text-xs font-medium text-blue-800">Tavsiyalar:</p>
                                                <p className="text-xs text-blue-700">{exec.recommendations}</p>
                                              </div>
                                            )}

                                            {/* Files */}
                                            {exec.files && exec.files.length > 0 && (
                                              <div className="flex flex-wrap gap-2 mt-2">
                                                {exec.files.map((file) => (
                                                  <a
                                                    key={file.id}
                                                    href={file.file_url || file.file}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="flex items-center gap-1 bg-gray-100 hover:bg-gray-200 px-2 py-1 rounded text-xs"
                                                  >
                                                    <PaperClipIcon className="w-3.5 h-3.5" />
                                                    {file.file_name}
                                                    <button
                                                      onClick={(e) => { e.preventDefault(); handleDeleteFile(exec.id, file.id); }}
                                                      className="ml-1 text-red-500 hover:text-red-700"
                                                    >
                                                      <XMarkIcon className="w-3.5 h-3.5" />
                                                    </button>
                                                  </a>
                                                ))}
                                              </div>
                                            )}
                                          </div>
                                          <div className="flex gap-1 ml-2">
                                            <button onClick={() => handleEditExecution(exec)} className="p-1.5 hover:bg-gray-100 rounded">
                                              <PencilIcon className="w-4 h-4 text-gray-600" />
                                            </button>
                                            <button onClick={() => confirmDelete('execution', exec)} className="p-1.5 hover:bg-gray-100 rounded">
                                              <TrashIcon className="w-4 h-4 text-red-600" />
                                            </button>
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <p className="text-sm text-gray-500 text-center py-4">Bajarilgan ishlar yo'q</p>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-center text-gray-500 py-8">Bandlar yo'q</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <EmptyState icon={CalendarIcon} title="Ish rejalar yo'q" description="Yangi ish reja yarating" />
      )}

      {/* Plan Modal */}
      <Modal isOpen={showPlanModal} onClose={() => setShowPlanModal(false)} title={selectedPlan ? 'Ish rejani tahrirlash' : 'Yangi ish reja'} size="lg">
        <form onSubmit={handlePlanSubmit} className="space-y-4">
          <div className="input-group">
            <label className="label">Nomi *</label>
            <input type="text" className="input" value={planForm.title} onChange={(e) => setPlanForm({ ...planForm, title: e.target.value })} required />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="input-group">
              <label className="label">Turi</label>
              <select className="select" value={planForm.plan_type} onChange={(e) => setPlanForm({ ...planForm, plan_type: e.target.value })}>
                <option value="otm">OTM</option>
                <option value="ministry">Vazirlik</option>
              </select>
            </div>
            {isAdmin && (
              <div className="input-group">
                <label className="label">Tashkilot *</label>
                <select className="select" value={planForm.organization} onChange={(e) => setPlanForm({ ...planForm, organization: e.target.value })} required>
                  <option value="">Tanlang</option>
                  {organizations.map(org => (
                    <option key={org.id} value={org.id}>{org.short_name || org.name}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="input-group">
              <label className="label">Yil</label>
              <select className="select" value={planForm.year} onChange={(e) => setPlanForm({ ...planForm, year: parseInt(e.target.value) })}>
                {[CURRENT_YEAR, CURRENT_YEAR + 1].map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <div className="input-group">
              <label className="label">Chorak</label>
              <select className="select" value={planForm.quarter} onChange={(e) => setPlanForm({ ...planForm, quarter: parseInt(e.target.value) })}>
                <option value={0}>Yillik</option>
                <option value={1}>1-chorak</option>
                <option value={2}>2-chorak</option>
                <option value={3}>3-chorak</option>
                <option value={4}>4-chorak</option>
              </select>
            </div>
          </div>
          <div className="input-group">
            <label className="label">Tavsif</label>
            <textarea className="textarea" value={planForm.description} onChange={(e) => setPlanForm({ ...planForm, description: e.target.value })} />
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button type="button" onClick={() => setShowPlanModal(false)} className="btn-secondary">Bekor qilish</button>
            <button type="submit" className="btn-primary">{selectedPlan ? 'Saqlash' : 'Yaratish'}</button>
          </div>
        </form>
      </Modal>

      {/* Item Modal */}
      <Modal isOpen={showItemModal} onClose={() => setShowItemModal(false)} title={selectedItem ? 'Bandni tahrirlash' : 'Yangi band'} size="lg">
        <form onSubmit={handleItemSubmit} className="space-y-4">
          <div className="input-group">
            <label className="label">Vazifa nomi *</label>
            <textarea className="textarea" rows={2} value={itemForm.title} onChange={(e) => setItemForm({ ...itemForm, title: e.target.value })} required />
          </div>
          <div className="input-group">
            <label className="label">Tavsif</label>
            <textarea className="textarea" value={itemForm.description} onChange={(e) => setItemForm({ ...itemForm, description: e.target.value })} />
          </div>
          <div className="input-group">
            <label className="label">Mas'ul shaxs</label>
            <input type="text" className="input" value={itemForm.responsible} onChange={(e) => setItemForm({ ...itemForm, responsible: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="input-group">
              <label className="label">Boshlanish sanasi *</label>
              <input type="date" className="input" value={itemForm.start_date} onChange={(e) => setItemForm({ ...itemForm, start_date: e.target.value })} required />
            </div>
            <div className="input-group">
              <label className="label">Tugash sanasi *</label>
              <input type="date" className="input" value={itemForm.end_date} onChange={(e) => setItemForm({ ...itemForm, end_date: e.target.value })} required />
            </div>
          </div>
          {selectedItem && (
            <div className="input-group">
              <label className="label">Holati</label>
              <select className="select" value={itemForm.status} onChange={(e) => setItemForm({ ...itemForm, status: e.target.value })}>
                <option value="pending">Kutilmoqda</option>
                <option value="in_progress">Bajarilmoqda</option>
                <option value="completed">Bajarildi</option>
                <option value="not_completed">Bajarilmadi</option>
              </select>
            </div>
          )}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button type="button" onClick={() => setShowItemModal(false)} className="btn-secondary">Bekor qilish</button>
            <button type="submit" className="btn-primary">{selectedItem ? 'Saqlash' : 'Qo\'shish'}</button>
          </div>
        </form>
      </Modal>

      {/* Execution Modal */}
      <Modal isOpen={showExecutionModal} onClose={() => setShowExecutionModal(false)} title={selectedExecution ? 'Bajarilgan ishni tahrirlash' : 'Bajarilgan ish qo\'shish'} size="lg">
        <form onSubmit={handleExecutionSubmit} className="space-y-4">
          <div className="input-group">
            <label className="label">Bajarilgan sana *</label>
            <input type="date" className="input" value={executionForm.execution_date} onChange={(e) => setExecutionForm({ ...executionForm, execution_date: e.target.value })} required />
          </div>
          <div className="input-group">
            <label className="label">Bajarilgan ish tavsifi *</label>
            <textarea className="textarea" rows={3} value={executionForm.description} onChange={(e) => setExecutionForm({ ...executionForm, description: e.target.value })} required />
          </div>
          <div className="input-group">
            <label className="label">Natija</label>
            <textarea className="textarea" value={executionForm.result} onChange={(e) => setExecutionForm({ ...executionForm, result: e.target.value })} />
          </div>
          
          <div className="border-t pt-4">
            <h4 className="font-medium text-gray-700 mb-3">Aniqlangan holatlar</h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="input-group">
                <label className="label">Holatlar soni</label>
                <input type="number" min="0" className="input" value={executionForm.findings_count} onChange={(e) => setExecutionForm({ ...executionForm, findings_count: parseInt(e.target.value) || 0 })} />
              </div>
            </div>
            <div className="input-group mt-3">
              <label className="label">Aniqlangan holatlar tavsifi</label>
              <textarea className="textarea" value={executionForm.findings_description} onChange={(e) => setExecutionForm({ ...executionForm, findings_description: e.target.value })} placeholder="Tekshiruvda aniqlangan holatlar..." />
            </div>
            <div className="input-group mt-3">
              <label className="label">Tavsiyalar</label>
              <textarea className="textarea" value={executionForm.recommendations} onChange={(e) => setExecutionForm({ ...executionForm, recommendations: e.target.value })} placeholder="Berilgan tavsiyalar..." />
            </div>
          </div>

          {/* File Upload */}
          <div className="border-t pt-4">
            <h4 className="font-medium text-gray-700 mb-3">Fayllar</h4>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
              <input type="file" multiple onChange={handleFileSelect} className="hidden" id="file-upload" />
              <label htmlFor="file-upload" className="flex flex-col items-center cursor-pointer">
                <ArrowUpTrayIcon className="w-8 h-8 text-gray-400 mb-2" />
                <span className="text-sm text-gray-600">Fayllarni tanlang</span>
              </label>
            </div>
            {uploadFiles.length > 0 && (
              <div className="mt-3 space-y-2">
                {uploadFiles.map((file, idx) => (
                  <div key={idx} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                    <div className="flex items-center gap-2">
                      <DocumentTextIcon className="w-5 h-5 text-gray-400" />
                      <span className="text-sm">{file.name}</span>
                      <span className="text-xs text-gray-500">{formatFileSize(file.size)}</span>
                    </div>
                    <button type="button" onClick={() => removeFile(idx)} className="text-red-500">
                      <XMarkIcon className="w-5 h-5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <button type="button" onClick={() => setShowExecutionModal(false)} className="btn-secondary">Bekor qilish</button>
            <button type="submit" className="btn-primary">{selectedExecution ? 'Saqlash' : 'Qo\'shish'}</button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirm */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        title="O'chirishni tasdiqlang"
        message={`Bu ${deleteType === 'plan' ? 'ish reja' : deleteType === 'item' ? 'band' : 'bajarilgan ish'}ni o'chirishni xohlaysizmi?`}
        confirmText="O'chirish"
      />
    </div>
  );
};

export default WorkPlans;
